import { useState, useEffect, useRef } from 'react';
import { Modal, message, FloatButton } from 'antd';
import { CustomerServiceOutlined, CommentOutlined, BulbOutlined, MailOutlined, PhoneOutlined, SendOutlined, WechatOutlined, CopyOutlined, QrcodeOutlined, MessageOutlined } from '@ant-design/icons';
import { t } from '../utils/i18n';
import { getApiUrl, getImageUrl } from '../utils/apiUrl';
import { copyToClipboard } from '../utils/clipboard';

const CustomerService = () => {
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');
  const [position, setPosition] = useState({ right: 20, bottomPercent: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const floatBtnRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  
  const [config, setConfig] = useState({
    telegram: '',
    whatsapp: '',
    email: 'support@blueapi.com',
    skype: '',
    wechat: '',
    zalo: '',
    enabled: true,
    wechat_qr_code: '',
  });

  // 从 localStorage 恢复位置
  useEffect(() => {
    const savedPosition = localStorage.getItem('customerServicePosition');
    if (savedPosition) {
      try {
        setPosition(JSON.parse(savedPosition));
      } catch (e) {
        console.error('Failed to parse saved position', e);
      }
    }
  }, []);

  const savePosition = (newPosition) => {
    localStorage.setItem('customerServicePosition', JSON.stringify(newPosition));
    setPosition(newPosition);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    // 记录拖动开始时的鼠标位置和按钮位置
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      right: position.right,
      bottomPercent: position.bottomPercent
    };
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    // 计算鼠标移动的距离
    const deltaX = e.clientX - dragStartRef.current.mouseX;
    const deltaY = e.clientY - dragStartRef.current.mouseY;
    
    // right 定位：鼠标往右移动，按钮往右移 = right 值减小
    let newRight = dragStartRef.current.right - deltaX;
    // bottom 百分比：鼠标往下移动，按钮往下移 = 百分比减小
    let newBottomPercent = dragStartRef.current.bottomPercent - (deltaY / window.innerHeight * 100);
    
    // 边界限制
    newRight = Math.max(0, Math.min(window.innerWidth - 60, newRight));
    newBottomPercent = Math.max(0, Math.min(80, newBottomPercent));
    
    savePosition({ right: newRight, bottomPercent: newBottomPercent });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, position]);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(getApiUrl('/api/customer-service'));
      const data = await res.json();
      if (data.code === 0) {
        setConfig(data.data);
      }
    } catch (error) {
      console.error(t('fetch_customer_service_failed'), error);
    }
  };

  const handleButtonClick = (tab) => {
    setActiveTab(tab);
    setVisible(true);
  };

  const handleModalClose = () => {
    setVisible(false);
  };

  return (
    <>
      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'fixed',
          right: position.right,
          bottom: `${position.bottomPercent}%`,
          cursor: isDragging ? 'grabbing' : 'move',
          zIndex: 1000,
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <FloatButton.Group
          trigger="click"
          type="default"
          icon={<CustomerServiceOutlined />}
          className="customer-service-group"
          style={{ position: 'static' }}
        >
          <FloatButton
            icon={<CustomerServiceOutlined />}
            tooltip={{ title: t('contact_customer_service'), placement: 'left' }}
            onClick={() => handleButtonClick('contact')}
          />
          <FloatButton
            icon={<CommentOutlined />}
            tooltip={{ title: t('feedback'), placement: 'left' }}
            onClick={() => handleButtonClick('feedback')}
          />
          <FloatButton
            icon={<BulbOutlined />}
            tooltip={{ title: t('model_square'), placement: 'left' }}
            onClick={() => {
              const token = localStorage.getItem('user_token');
              if (token) {
                window.location.href = '/dashboard/models';
              } else {
                window.location.href = '/login?redirect=/dashboard/models';
              }
            }}
            style={{ marginBottom: 14 }}
          />
        </FloatButton.Group>
      </div>

      <Modal
        title={activeTab === 'activity' ? t('model_recommend') : activeTab === 'feedback' ? t('feedback') : t('contact_customer_service')}
        open={visible}
        onCancel={handleModalClose}
        footer={null}
        width={360}
        centered
        maskClosable={true}
        afterClose={handleModalClose}
        styles={{
          body: { padding: '12px 16px' },
          wrapper: { overflow: 'hidden' }
        }}
      >
        {activeTab === 'activity' && <ModelRecommend />}
        {activeTab === 'feedback' && <FeedbackForm onSuccess={() => setVisible(false)} />}
        {activeTab === 'contact' && <ContactInfo config={config} />}
      </Modal>

      <style>{`
        .customer-service-group .ant-float-btn {
          width: 56px;
          height: 56px;
          background: #fff;
          border: none;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }
        .customer-service-group .ant-float-btn:hover {
          background: #f5f5f5;
        }
        .customer-service-group .ant-float-btn-group {
          background: transparent !important;
          border: none !important;
          position: relative !important;
        }
        .customer-service-group .ant-float-btn-group::before {
          display: none;
        }
        .customer-service-group .ant-float-btn-wrap {
          position: relative !important;
        }
        /* 隐藏展开后关闭按钮的 tooltip */
        .customer-service-group .ant-float-btn-group-close span[role="img"] + div,
        .customer-service-group .ant-float-btn-group-close .ant-tooltip,
        .customer-service-group .ant-float-btn-group-close [class*="Tooltip"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        body {
          scrollbar-gutter: stable;
        }
      `}</style>
    </>
  );
};

// 模型推荐
const ModelRecommend = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const token = localStorage.getItem('user_token');
      const res = await fetch(getApiUrl('/api/models'), {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.code === 0) {
        setModels(data.data || []);
      }
    } catch (error) {
      console.error(t('fetch_models_failed'), error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={styles.loading}>{t('loading')}</div>;
  if (models.length === 0) return <div style={styles.empty}>{t('no_available_models', 'No available models')}</div>;

  return (
    <div style={styles.modelList}>
      {models.map((model) => (
        <div key={model.id} style={styles.modelCard}>
          <div style={styles.modelName}>{model.name}</div>
          <div style={styles.modelDesc}>{model.description || t('no_description', 'No description')}</div>
          <div style={styles.modelPrice}>
            ${model.price_per_1k_tokens?.input || '0'} / 1K tokens
          </div>
        </div>
      ))}
    </div>
  );
};

// 问题反馈表单
const FeedbackForm = ({ onSuccess }) => {
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) {
      message.warning(t('please_enter_feedback', 'Please enter feedback'));
      return;
    }
    const token = localStorage.getItem('user_token');
    if (!token) {
      message.warning(t('please_login_first', 'Please login first'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(getApiUrl('/api/feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ type: 'feedback', content }),
      });
      const data = await res.json();
      if (data.code === 0) {
        message.success(t('feedback_submitted', 'Feedback submitted'));
        onSuccess();
      } else {
        message.error(data.message || t('submit_failed', 'Submit failed'));
      }
    } catch (error) {
      message.error(t('submit_failed', 'Submit failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.feedbackForm}>
      <textarea
        style={styles.textarea}
        placeholder={t('feedback_description', 'Please enter your questions or suggestions')}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={5}
      />
      <button style={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
        {submitting ? t('submitting') : t('submit')}
      </button>
    </div>
  );
};

// 客服联系方式展示
const ContactInfo = ({ config }) => {
  const [wechatModalVisible, setWechatModalVisible] = useState(false);

  const getContactItems = () => {
    const items = [];

    if (config.email) {
      items.push({
        icon: <MailOutlined />,
        label: t('email'),
        value: config.email,
        type: 'copy',
        color: '#1890ff',
      });
    }
    if (config.whatsapp) {
      items.push({
        icon: <PhoneOutlined />,
        label: 'WhatsApp',
        value: config.whatsapp,
        href: `https://wa.me/${config.whatsapp.replace(/\D/g, '')}`,
        type: 'link',
        color: '#25D366',
      });
    }
    if (config.telegram) {
      items.push({
        icon: <SendOutlined />,
        label: 'Telegram',
        value: config.telegram,
        href: config.telegram.startsWith('@') ? `https://t.me/${config.telegram.slice(1)}` : config.telegram,
        type: 'link',
        color: '#0088cc',
      });
    }
    if (config.skype) {
      items.push({
        icon: <MessageOutlined />,
        label: 'Skype',
        value: config.skype,
        href: `skype:${config.skype}?chat`,
        type: 'link',
        color: '#00AFF0',
      });
    }
    if (config.wechat) {
      items.push({
        icon: <WechatOutlined />,
        label: t('wechat_service', 'WeChat Service'),
        value: config.wechat,
        type: 'wechat',
        color: '#07C160',
      });
    }
    if (config.zalo) {
      items.push({
        icon: <PhoneOutlined />,
        label: 'Zalo',
        value: config.zalo,
        href: `https://zalo.me/${config.zalo}`,
        type: 'link',
        color: '#0068FF',
      });
    }
    return items;
  };

  const handleClick = async (item) => {
    if (item.type === 'link' && item.href) {
      window.open(item.href, '_blank');
    } else if (item.type === 'wechat') {
      setWechatModalVisible(true);
    } else if (item.type === 'copy') {
      const success = await copyToClipboard(item.value);
      if (success) {
        message.success(t('copied_to_clipboard', 'Copied to clipboard'));
      }
    }
  };

  const contactItems = getContactItems();

  if (contactItems.length === 0) {
    return (
      <div style={styles.emptyContact}>
        <CustomerServiceOutlined style={{ fontSize: 40, color: '#ddd', marginBottom: 12 }} />
        <p style={{ fontSize: 14, marginBottom: 4 }}>{t('no_contact_info', 'No contact info')}</p>
        <p style={{ fontSize: 12, color: '#999' }}>{t('contact_admin_config', 'Please contact admin to configure')}</p>
      </div>
    );
  }

  return (
    <>
      <div style={styles.contactInfoWrap}>
        <div style={styles.contactList}>
          {contactItems.map((item, index) => (
            <div
              key={index}
              style={styles.contactItem}
              onClick={() => handleClick(item)}
            >
              <div style={{ ...styles.itemIcon, backgroundColor: `${item.color}15`, color: item.color }}>
                {item.icon}
              </div>
              <div style={styles.itemContent}>
                <span style={styles.itemLabel}>{item.label}</span>
                <span style={styles.itemValue}>{item.value}</span>
              </div>
              {item.type === 'copy' && (
                <CopyOutlined style={styles.copyIcon} />
              )}
              {item.type === 'wechat' && (
                <QrcodeOutlined style={styles.copyIcon} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 微信二维码弹窗 */}
      <Modal
        title={t('wechat_service', 'WeChat Service')}
        open={wechatModalVisible}
        onCancel={() => setWechatModalVisible(false)}
        footer={null}
        width={300}
        centered
        styles={{
          body: { textAlign: 'center', padding: '16px' }
        }}
      >
        <div style={styles.wechatModal}>
          {config.wechat_qr_code ? (
            <>
              <img 
                src={getImageUrl(config.wechat_qr_code)}
                alt={t('wechat_service', 'WeChat Service')} 
                style={styles.wechatQrcode}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                style={{ ...styles.wechatIdBox, display: 'none' }}
              >
                <WechatOutlined style={{ fontSize: 48, color: '#07C160', marginBottom: 12 }} />
                <p style={styles.wechatIdLabel}>{t('wechat_customer_id', 'WeChat Customer ID')}</p>
                <p style={styles.wechatIdValue}>{config.wechat || t('none', 'None')}</p>
                <button
                  style={styles.copyBtn}
                  onClick={async () => {
                    if (config.wechat) {
                      const success = await copyToClipboard(config.wechat);
                      if (success) {
                        message.success(t('wechat_copied', 'WeChat ID copied'));
                      }
                    }
                  }}
                >
                  {t('copy_wechat_customer_id', 'Copy WeChat Customer ID')}
                </button>
              </div>
            </>
          ) : (
            <div style={styles.wechatIdBox}>
              <WechatOutlined style={{ fontSize: 48, color: '#07C160', marginBottom: 12 }} />
              <p style={styles.wechatIdLabel}>{t('wechat_id', 'WeChat ID')}</p>
              <p style={styles.wechatIdValue}>{config.wechat || t('none', 'None')}</p>
              {config.wechat && (
                <button
                  style={styles.copyBtn}
                  onClick={async () => {
                    const success = await copyToClipboard(config.wechat);
                    if (success) {
                      message.success(t('wechat_copied', 'WeChat ID copied'));
                    }
                  }}
                >
                  {t('copy_wechat_customer_id', 'Copy WeChat Customer ID')}
                </button>
              )}
            </div>
          )}
          <p style={styles.wechatTip}>{t('scan_or_search', 'Scan or search WeChat ID to add customer service')}</p>
        </div>
      </Modal>
    </>
  );
};

const styles = {
  loading: {
    textAlign: 'center',
    padding: '24px',
    color: '#999',
  },
  empty: {
    textAlign: 'center',
    padding: '24px',
    color: '#999',
  },
  modelList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  modelCard: {
    padding: '12px',
    backgroundColor: '#f8f8f8',
    borderRadius: '8px',
  },
  modelName: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    marginBottom: '4px',
  },
  modelDesc: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '4px',
  },
  modelPrice: {
    fontSize: '13px',
    color: '#1890ff',
    fontWeight: 'bold',
  },
  feedbackForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'none',
    fontFamily: 'inherit',
  },
  submitBtn: {
    padding: '10px 24px',
    backgroundColor: '#1a1a2e',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    width: '100%',
    marginTop: '4px',
  },
  contactInfoWrap: {
    padding: '4px 0',
  },
  contactList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    backgroundColor: '#f8f9fa',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  itemIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    marginRight: '12px',
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemLabel: {
    display: 'block',
    fontSize: '12px',
    color: '#999',
    marginBottom: '2px',
  },
  itemValue: {
    display: 'block',
    fontSize: '14px',
    color: '#333',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  copyIcon: {
    fontSize: '14px',
    color: '#ccc',
    marginLeft: '8px',
  },
  emptyContact: {
    textAlign: 'center',
    padding: '32px 16px',
  },
  wechatModal: {
    textAlign: 'center',
  },
  wechatQrcode: {
    width: '180px',
    height: '180px',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  wechatIdBox: {
    padding: '16px 0',
  },
  wechatIdLabel: {
    fontSize: '12px',
    color: '#999',
    marginBottom: '4px',
  },
  wechatIdValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '16px',
  },
  copyBtn: {
    padding: '8px 20px',
    backgroundColor: '#07C160',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  wechatTip: {
    fontSize: '12px',
    color: '#999',
    marginTop: '8px',
  },
};

export default CustomerService;
